import React, { useRef } from "react";
import { Box, Container, Typography, Paper, Stack, useTheme, useMediaQuery } from "@mui/material";
import { Brain, Zap } from "lucide-react";
import { motion, useInView } from "framer-motion";
import { COLORS } from "constants/colors";

const algorithms = [
  {
    icon: Brain,
    title: "Analysis Selection Algorithm",
    description:
      "Translates your question and evaluates your dataset to choose the most appropriate statistical methods—ensuring accurate, expert-level decisions without requiring statistical expertise.",
    iconStroke: "#7C3AED",
    iconBg: "#EDE9FE",
  },
  {
    icon: Zap,
    title: "Analysis Execution Algorithm",
    description:
      "Performs the selected analyses at scale, automating calculations and delivering clean, publication-ready results in seconds.",
    iconStroke: "#CA8A04",
    iconBg: "#FEF08A",
  },
];

export const AlgorithmsSection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: false, margin: "-100px" });
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const getCardDimensions = () => {
    if (isSmallMobile) {
      return { width: "100%", height: 280 };
    } else if (isMobile) {
      return { width: 280, height: 300 };
    }
    return { width: 350, height: 300 };
  };

  const { width: cardWidth, height: cardHeight } = getCardDimensions();

  return (
    <Stack
      component="section"
      sx={{
        backgroundColor: "#ffffff",
        py: { xs: 6, md: 12 },
        px: { xs: "24px", md: 6 },
        width: "100%",
        boxSizing: "border-box",
        gap: "10px",
      }}
      id="algorithms"
    >
      <Container ref={ref} gap="10px" sx={{ textAlign: "center", px: { xs: 0, md: 2 } }}>
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
        >
          <Typography
            variant={isMobile ? "h4" : "h3"}
            fontWeight="bold"
            mb={2}
            color="#1A1A1A"
            sx={{ px: { xs: 1, sm: 0 } }}
          >
            Intelligence at the Core
          </Typography>
          <Typography
            variant={isMobile ? "h6" : "h5"}
            color={COLORS.gray900}
            mb={{ xs: 4, md: 6 }}
            sx={{
              fontWeight: 400,
              maxWidth: 700,
              mx: "auto",
              lineHeight: 1.5,
              px: { xs: 1, sm: 0 },
            }}
          >
            Genie's dual, non-AI algorithms were built for speed, precision, and consistency.
          </Typography>
        </motion.div>

        {/* Responsive grid container */}
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: "repeat(2, 1fr)",
              md: `repeat(2, ${cardWidth}px)`,
            },
            gridAutoRows: {
              xs: `${cardHeight}px`,
              sm: `${cardHeight}px`,
              md: `${cardHeight}px`,
            },
            justifyContent: "center",
            gap: { xs: 4, md: 4 },
            boxSizing: "border-box",
            width: "100%",
            maxWidth: { xs: "100%", md: "auto" },
          }}
        >
          {algorithms.map((algo, index) => (
            <Box
              key={index}
              sx={{
                position: "relative",
                pt: 5,
                width: "100%",
                height: cardHeight,
                boxSizing: "border-box",
                justifySelf: "center",
                maxWidth: { xs: "100%", sm: cardWidth, md: cardWidth },
              }}
            >
              {/* Icon */}
              <Box
                sx={{
                  position: "absolute",
                  top: 30,
                  left: "50%",
                  transform: "translate(-50%, -50%)",
                  backgroundColor: algo.iconBg,
                  p: { xs: 1.5, sm: 2 },
                  borderRadius: "50%",
                  boxShadow: 3,
                  zIndex: 2,
                }}
              >
                <algo.icon size={isMobile ? 24 : 32} color={algo.iconStroke} strokeWidth={2} />
              </Box>

              {/* Card with animation */}
              <motion.div
                initial={{ opacity: 0, y: 40 }}
                animate={isInView ? { opacity: 1, y: 0 } : {}}
                whileHover={{ scale: isMobile ? 1.02 : 1.03 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                style={{ width: "100%", height: "100%" }}
              >
                <Paper
                  elevation={4}
                  sx={{
                    width: "100%",
                    maxWidth: { xs: "300px", sm: "100%" },
                    mx: "auto",
                    height: "100%",
                    p: { xs: 2, sm: 3, md: 4 },
                    pt: { xs: 4, sm: 5, md: 6 },
                    display: "flex",
                    flexDirection: "column",
                    backgroundColor: COLORS.cardsBackground,
                    alignItems: "flex-start",
                    textAlign: "left",
                    borderRadius: 4,
                    position: "relative",
                    zIndex: 1,
                    boxSizing: "border-box",
                    border: "1px solid rgb(233, 236, 241, 0.5)",
                  }}
                >
                  <Typography
                    variant={isMobile ? "subtitle1" : "h6"}
                    fontWeight="bold"
                    mb={{ xs: 2, sm: 3 }}
                    sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                  >
                    {algo.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" lineHeight={1.6}>
                    {algo.description}
                  </Typography>
                </Paper>
              </motion.div>
            </Box>
          ))}
        </Box>
      </Container>
    </Stack>
  );
};
